import React, { useContext } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  Callout,
  Box as Card,
  Box as Container,
  Box as DetailsContainer,
} from '@node-space/ui-components'
import { logSentryErrorAndGetEventId } from '@node-space/utils'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { Approval } from 'constants/General'
import { usePutConfirmPaymentMutation } from 'hooks/mutations/usePutConfirmPaymentMutation'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useMinutesSecondsTimer } from 'hooks/useMinutesSecondsTimer'
import { contactAtom, contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { SendStep } from 'types/walletActions'
import { formatString, roundNumberWithCommas } from 'utils/utils'
import { paymentAtom, sendStepAtom, sendWalletAtom } from './atoms/atoms'

export const SendReview = () => {
  const { t } = useTranslation()
  const contact = useAtomValue(contactAtom)
  const sendWallet = useAtomValue(sendWalletAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const payment = useAtomValue(paymentAtom)
  const setSendStep = useSetAtom(sendStepAtom)
  const { mutate: putConfirmPayment, isPending: isConfirmingPayment } =
    usePutConfirmPaymentMutation()
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { approvalsIsRequiredForFlow } = useApprovalsConfigForFlow(Approval.FLOWS.CRYPTO_PAYOUT)
  const [walletAction] = useContext(WalletActionContext)

  const isPegged = payment?.pegged ?? false
  const peggedAmount = payment?.pegStablecoinDetails?.paidCurrency?.amount ?? 0
  const peggedRate = payment?.pegStablecoinDetails?.exchangeRate?.rate ?? 0

  const handleConfirmPayment = () => {
    resetRequestError()
    putConfirmPayment(payment?.uuid, {
      onSuccess: () => {
        if (approvalsIsRequiredForFlow) {
          setSendStep(SendStep.APPROVAL)
        } else {
          setSendStep(SendStep.COMPLETE)
        }
      },
      onError: (error: BaseErrorResponse) => {
        let errorMessage = ''

        if (error?.errorList?.[0]?.code === 'MER-PAY-2012') {
          errorMessage = t('wallets.send.walletInsufficientFunds')
        }

        const sentryEventId = logSentryErrorAndGetEventId(
          `Failed API request to /v1/pay/${payment?.uuid}/confirm/summary with status ${error?.status} - usePutConfirmPaymentMutation`,
          error,
          {
            data: payment?.uuid,
          }
        )

        setRequestError({
          handledMessage: errorMessage || error?.message,
          errorCode: error?.status,
          sentryTraceId: sentryEventId,
          show: true,
        })
      },
    })
  }

  return (
    <Container width="full" data-testid="review-container">
      {requestError?.show && (
        <Box paddingT={4} paddingB={16}>
          <RequestErrorCallout requestError={requestError} />
        </Box>
      )}
      <CountdownCallout onExpiry={walletAction?.onClose} />
      <Card background="subtle" padding={16}>
        <DetailsContainer flexCol align="center" gapY={8}>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.sendingFromWallet')}
              </p>
              <Box flex gapX={4} align="center" className="text-end">
                <Avatar
                  size="small"
                  type="currency"
                  name={sendWallet?.currency ? sendWallet?.currency?.code?.toLowerCase() : ''}
                />
                <p className="text-label text-foreground-prominent">
                  {`${sendWallet?.description} | ${formatString(
                    t('balanceWithCurrency'),
                    roundNumberWithCommas(sendWallet?.balance),
                    sendWallet?.currency?.code
                  )}`}
                </p>
              </Box>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.sendingTo')}
              </p>
              <p className="text-label text-foreground-prominent">
                {`${contact?.name ? contact?.name : ''} | ${contactInstrument?.address}`}
              </p>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.uniqueReference')}
              </p>
              <p className="text-label text-foreground-prominent">{payment?.reference}</p>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.beneficiaryWillReceive')}
              </p>
              <p className="text-label text-foreground-prominent">{`${isPegged ? peggedAmount : payment?.paidCurrency?.amount} ${payment?.paidCurrency?.currency}`}</p>
            </Box>
          </Box>
          <Box width="full" className="border border-stroke-static" />
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.amountToSend')}
              </p>
              <p className="text-label text-foreground-prominent">{`${payment?.walletCurrency?.amount} ${payment?.walletCurrency?.currency}`}</p>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.exchangeRate')}
              </p>
              <p className="text-label text-foreground-prominent">
                {`1 ${payment?.exchangeRate?.base} = ${isPegged ? peggedRate : payment?.exchangeRate?.rate} ${payment?.exchangeRate?.counter}`}
              </p>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.processingFee')}
              </p>
              <p className="text-label text-foreground-prominent">
                {`${payment?.feeCurrency?.amount} ${payment?.feeCurrency?.currency}`}
              </p>
            </Box>
          </Box>
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.networkFee')}
              </p>
              <p className="text-label text-foreground-prominent">
                {`${payment?.networkFeeCurrency?.amount} ${payment?.networkFeeCurrency?.currency}`}
              </p>
            </Box>
          </Box>
          <Box width="full" className="border border-stroke-static" />
          <Box flexCol paddingT={4} gapY={8} width="full">
            <Box flex justify="between" gapX={16}>
              <p className="text-body-regular text-foreground-subdued">
                {t('wallets.send.totalSubtracted')}
              </p>
              <p className="text-label text-foreground-prominent">
                {`${payment?.totalWalletAmount} ${payment?.walletCurrency?.currency}`}
              </p>
            </Box>
          </Box>
        </DetailsContainer>
      </Card>
      <Box flex justify="between" paddingT={16}>
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="complete-payment"
          type="button"
          variant="primary"
          onClick={handleConfirmPayment}
          loading={isConfirmingPayment}
        >
          {t('wallets.send.completePayment')}
        </Button>
      </Box>
    </Container>
  )
}
const CountdownCallout = ({ onExpiry }: { onExpiry: () => void }) => {
  const { t } = useTranslation()
  const { formattedTime } = useMinutesSecondsTimer(5, 0, true, onExpiry)

  return (
    <Callout variant="warning">
      {t('contacts.paymentWillExpireInFiveMinutes', {
        minutes: `${formattedTime.minutes}:${formattedTime.seconds}`,
      })}
    </Callout>
  )
}
